import { useEffect, useMemo, useRef, useState } from 'react';
import apiService from '../services/api';
import {
  DEFAULT_COLORS,
  DEFAULT_LOGO_SIZE,
  mapLogoSizeFromApi,
  normalizeHexInput,
  normalizeLogoSizeInput,
  toPickerValue,
} from '../utils/embedAppearanceColors';
import '../styles/EmbedAppearanceSection.css';

const sectionStyle = {
  marginTop: '32px',
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '16px',
  padding: '24px',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
};

const uploadBoxStyle = {
  border: '2px dashed #e2e8f0',
  borderRadius: '12px',
  padding: '20px',
  textAlign: 'center',
  background: '#f8fafc',
};

const uploadActionsStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
  justifyContent: 'center',
  alignItems: 'center',
};

const colorInputStyle = {
  width: '48px',
  height: '40px',
  padding: 0,
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  backgroundColor: 'transparent',
  cursor: 'pointer',
};

const colorHexStyle = {
  width: '140px',
  padding: '10px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  fontFamily: 'ui-monospace, monospace',
  fontSize: '14px',
};

function AppearanceColorField({
  id,
  label,
  hint,
  value,
  fallback,
  onChange,
}) {
  return (
    <div style={{ marginTop: '24px' }}>
      <label
        htmlFor={id}
        style={{ display: 'block', fontWeight: 700, marginBottom: '8px' }}
      >
        {label}
      </label>
      {hint ? (
        <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '14px' }}>
          {hint}
        </p>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <input
          type="color"
          value={toPickerValue(value)}
          onChange={(e) => onChange(normalizeHexInput(e.target.value) || fallback)}
          style={colorInputStyle}
          aria-label={label}
        />
        <input
          type="text"
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => onChange(normalizeHexInput(value) || fallback)}
          placeholder={fallback}
          style={colorHexStyle}
        />
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            backgroundColor: toPickerValue(value),
          }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

const EmbedAppearanceSection = ({ schoolId }) => {
  const logoInputRef = useRef(null);
  const welcomeImageInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [headerLogoUrl, setHeaderLogoUrl] = useState('');
  const [headerLogoFile, setHeaderLogoFile] = useState(null);
  const [welcomeScreenImageUrl, setWelcomeScreenImageUrl] = useState('');
  const [welcomeScreenImageFile, setWelcomeScreenImageFile] = useState(null);
  const [headerLogoSize, setHeaderLogoSize] = useState(DEFAULT_LOGO_SIZE);
  const [schoolColor, setSchoolColor] = useState(DEFAULT_COLORS.button);
  const [inAppColor, setInAppColor] = useState(DEFAULT_COLORS.inApp);
  const [inAppAccentColor, setInAppAccentColor] = useState(DEFAULT_COLORS.inAppAccent);

  useEffect(() => {
    if (!schoolId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const data = await apiService.getSchoolEmbedAppearance(schoolId);
        if (cancelled) return;
        setHeaderLogoUrl(data.headerLogoUrl || '');
        setWelcomeScreenImageUrl(
          data.welcomeScreenImageUrl ||
            data.loginBackgroundUrl ||
            data.emailSelectionBackgroundUrl ||
            '',
        );
        setHeaderLogoSize(mapLogoSizeFromApi(data));
        setSchoolColor(
          normalizeHexInput(data.button) || DEFAULT_COLORS.button,
        );
        setInAppColor(
          normalizeHexInput(data.inApp || data.inAppColor) || DEFAULT_COLORS.inApp,
        );
        setInAppAccentColor(
          normalizeHexInput(data.inAppAccent || data.inAppAccentColor) ||
            DEFAULT_COLORS.inAppAccent,
        );
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.error ||
              err.response?.data?.detail ||
              err.message ||
              'Failed to load embed appearance.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  const handleHeaderLogoFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setHeaderLogoFile(file);
      setSuccess('');
    }
  };

  const handleRemoveHeaderLogo = () => {
    setHeaderLogoFile(null);
    setHeaderLogoUrl('');
    setSuccess('');
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const handleWelcomeScreenImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setWelcomeScreenImageFile(file);
      setSuccess('');
    }
  };

  const handleRemoveWelcomeScreenImage = () => {
    setWelcomeScreenImageFile(null);
    setWelcomeScreenImageUrl('');
    setSuccess('');
    if (welcomeImageInputRef.current) {
      welcomeImageInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!schoolId) return;

    const normalizedColor = normalizeHexInput(schoolColor);
    const normalizedInAppColor = normalizeHexInput(inAppColor);
    const normalizedInAppAccentColor = normalizeHexInput(inAppAccentColor);
    if (!normalizedColor) {
      setError('Enter a valid hex color for School color.');
      return;
    }
    if (!normalizedInAppColor) {
      setError('Enter a valid hex color for In-app color.');
      return;
    }
    if (!normalizedInAppAccentColor) {
      setError('Enter a valid hex color for In-app accent color.');
      return;
    }

    if (headerLogoSize !== '' && headerLogoSize !== null && headerLogoSize !== undefined) {
      const parsedLogoSize = Number(headerLogoSize);
      if (!Number.isFinite(parsedLogoSize) || parsedLogoSize < 10 || parsedLogoSize > 200) {
        setError('Enter a valid logo size (10–200%) for Logo size.');
        return;
      }
    }

    const payload = {
      button: normalizedColor,
      inApp: normalizedInAppColor,
      inAppAccent: normalizedInAppAccentColor,
      headerLogoSize: normalizeLogoSizeInput(headerLogoSize, DEFAULT_LOGO_SIZE),
    };

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      let nextHeaderLogoUrl = headerLogoUrl || '';
      let nextWelcomeScreenImageUrl = welcomeScreenImageUrl || '';

      if (headerLogoFile) {
        const uploadResponse = await apiService.uploadSchoolEmbedHeaderLogo(
          schoolId,
          headerLogoFile,
        );
        if (uploadResponse.headerLogoUrl) {
          nextHeaderLogoUrl = uploadResponse.headerLogoUrl;
          setHeaderLogoUrl(uploadResponse.headerLogoUrl);
        }
        setHeaderLogoFile(null);
      }

      if (welcomeScreenImageFile) {
        const uploadResponse = await apiService.uploadSchoolEmbedWelcomeScreenImage(
          schoolId,
          welcomeScreenImageFile,
        );
        if (uploadResponse.welcomeScreenImageUrl) {
          nextWelcomeScreenImageUrl = uploadResponse.welcomeScreenImageUrl;
          setWelcomeScreenImageUrl(uploadResponse.welcomeScreenImageUrl);
        }
        setWelcomeScreenImageFile(null);
      }

      payload.headerLogoUrl = nextHeaderLogoUrl;
      payload.welcomeScreenImageUrl = nextWelcomeScreenImageUrl;

      const data = await apiService.updateSchoolEmbedAppearance(schoolId, payload);
      setHeaderLogoUrl(data.headerLogoUrl || nextHeaderLogoUrl);
      setWelcomeScreenImageUrl(
        data.welcomeScreenImageUrl || nextWelcomeScreenImageUrl,
      );
      setHeaderLogoSize(mapLogoSizeFromApi(data));
      setSchoolColor(normalizeHexInput(data.button) || normalizedColor);
      setInAppColor(
        normalizeHexInput(data.inApp || data.inAppColor) || normalizedInAppColor,
      );
      setInAppAccentColor(
        normalizeHexInput(data.inAppAccent || data.inAppAccentColor) ||
          normalizedInAppAccentColor,
      );
      setSuccess('Embed appearance saved.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          err.message ||
          'Failed to save embed appearance.'
      );
    } finally {
      setSaving(false);
    }
  };

  const previewHeaderLogo = useMemo(() => {
    if (headerLogoFile) {
      return URL.createObjectURL(headerLogoFile);
    }
    return headerLogoUrl || '';
  }, [headerLogoFile, headerLogoUrl]);

  const previewWelcomeScreenImage = useMemo(() => {
    if (welcomeScreenImageFile) {
      return URL.createObjectURL(welcomeScreenImageFile);
    }
    return welcomeScreenImageUrl || '';
  }, [welcomeScreenImageFile, welcomeScreenImageUrl]);

  useEffect(() => {
    if (!headerLogoFile) return undefined;
    return () => {
      URL.revokeObjectURL(previewHeaderLogo);
    };
  }, [headerLogoFile, previewHeaderLogo]);

  useEffect(() => {
    if (!welcomeScreenImageFile) return undefined;
    return () => {
      URL.revokeObjectURL(previewWelcomeScreenImage);
    };
  }, [welcomeScreenImageFile, previewWelcomeScreenImage]);

  return (
    <section style={sectionStyle}>
      <div>
        <h2 style={{ margin: 0, fontSize: '22px' }}>Embed Appearance</h2>
        <p style={{ margin: '8px 0 0', color: '#64748b', maxWidth: '720px' }}>
          Customize school color, in-app colors, welcome screen image, and embed header logo.
        </p>
      </div>

      {loading ? (
        <p style={{ color: '#64748b', marginTop: '24px' }}>Loading embed appearance...</p>
      ) : (
        <>
          {error && (
            <div style={{
              marginTop: '20px',
              background: '#fef2f2',
              color: '#b91c1c',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              padding: '12px 14px',
            }}>
              {error}
            </div>
          )}

          <div style={{ marginTop: '24px' }}>
            <label
              htmlFor="embed-school-color"
              style={{ display: 'block', fontWeight: 700, marginBottom: '8px' }}
            >
              School color
            </label>
            <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '14px' }}>
              Primary brand color used across the embed.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <input
                type="color"
                value={toPickerValue(schoolColor)}
                onChange={(e) => {
                  setSchoolColor(normalizeHexInput(e.target.value) || DEFAULT_COLORS.button);
                  setSuccess('');
                }}
                style={{
                  width: '48px',
                  height: '40px',
                  padding: 0,
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                }}
                aria-label="School color"
              />
              <input
                type="text"
                id="embed-school-color"
                value={schoolColor}
                onChange={(e) => {
                  setSchoolColor(e.target.value);
                  setSuccess('');
                }}
                onBlur={() =>
                  setSchoolColor(
                    normalizeHexInput(schoolColor) || DEFAULT_COLORS.button,
                  )
                }
                placeholder="#00C8FF"
                style={{
                  width: '140px',
                  padding: '10px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: '14px',
                }}
              />
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  backgroundColor: toPickerValue(schoolColor),
                }}
                aria-hidden="true"
              />
            </div>
          </div>

          <div style={{ marginTop: '28px' }}>
            <h3 style={{ margin: 0, fontSize: '18px' }}>In-app colors</h3>
            <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: '14px', maxWidth: '720px' }}>
              Used after sign-in. In-app color replaces the turquoise UI. Accent is the text on those turquoise fills.
            </p>
            <AppearanceColorField
              id="embed-in-app-color"
              label="In-app color"
              hint="Replaces the turquoise blue throughout the logged-in app."
              value={inAppColor}
              fallback={DEFAULT_COLORS.inApp}
              onChange={(next) => {
                setInAppColor(next);
                setSuccess('');
              }}
            />
            <AppearanceColorField
              id="embed-in-app-accent-color"
              label="In-app accent color"
              hint="Text and icon color on top of in-app blue buttons, badges, and chips."
              value={inAppAccentColor}
              fallback={DEFAULT_COLORS.inAppAccent}
              onChange={(next) => {
                setInAppAccentColor(next);
                setSuccess('');
              }}
            />
          </div>

          <div style={{ marginTop: '28px' }}>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px' }}>
              Welcome screen image
            </label>
            {previewWelcomeScreenImage && (
              <div style={{ marginBottom: '16px' }}>
                <img
                  src={previewWelcomeScreenImage}
                  alt="Welcome screen preview"
                  style={{
                    display: 'block',
                    maxWidth: '280px',
                    maxHeight: '320px',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    background: '#f8fafc',
                    padding: '8px',
                  }}
                />
              </div>
            )}
            <div style={uploadBoxStyle}>
              <input
                ref={welcomeImageInputRef}
                type="file"
                accept="image/*"
                onChange={handleWelcomeScreenImageSelect}
                style={{ display: 'none' }}
              />
              <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '14px' }}>
                Fan card image shown inside the phone on the get-started screen. Leave empty to use the default card.
              </p>
              <div style={uploadActionsStyle}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => welcomeImageInputRef.current?.click()}
                >
                  Choose welcome screen image
                </button>
                {(welcomeScreenImageFile || welcomeScreenImageUrl) && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleRemoveWelcomeScreenImage}
                  >
                    Remove welcome screen image
                  </button>
                )}
              </div>
              {welcomeScreenImageFile && (
                <p style={{ margin: '12px 0 0', color: '#334155', fontSize: '14px' }}>
                  Selected: {welcomeScreenImageFile.name}
                </p>
              )}
            </div>
          </div>

          <div style={{ marginTop: '28px' }}>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px' }}>Header logo</label>
            {previewHeaderLogo && (
              <div style={{ marginBottom: '16px' }}>
                <img
                  src={previewHeaderLogo}
                  alt="Embed header logo"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '100px',
                    objectFit: 'contain',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    padding: '8px',
                    background: '#f8fafc',
                  }}
                />
              </div>
            )}
            <div style={uploadBoxStyle}>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleHeaderLogoFileSelect}
                style={{ display: 'none' }}
              />
              <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '14px' }}>
                Upload a PNG or JPG for the embed header bar. Not the school logo from your profile or dashboard.
              </p>
              <div style={uploadActionsStyle}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => logoInputRef.current?.click()}
                >
                  Choose Header Logo
                </button>
                {(headerLogoFile || headerLogoUrl) && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleRemoveHeaderLogo}
                  >
                    Remove Header Logo
                  </button>
                )}
              </div>
              {headerLogoFile && (
                <p style={{ margin: '12px 0 0', color: '#334155', fontSize: '14px' }}>
                  Selected: {headerLogoFile.name}
                </p>
              )}
            </div>

            <div style={{ marginTop: '20px' }}>
              <label
                htmlFor="embed-header-logo-size"
                style={{ display: 'block', fontWeight: 700, marginBottom: '8px' }}
              >
                Logo size
              </label>
              <div className="embed-appearance-blur-input-group">
                <input
                  type="range"
                  className="embed-appearance-blur-slider"
                  min={10}
                  max={200}
                  value={normalizeLogoSizeInput(headerLogoSize, DEFAULT_LOGO_SIZE)}
                  onChange={(e) => setHeaderLogoSize(e.target.value)}
                  aria-label="Logo size"
                />
                <input
                  type="number"
                  id="embed-header-logo-size"
                  className="embed-appearance-blur-value"
                  min={10}
                  max={200}
                  value={headerLogoSize}
                  onChange={(e) => setHeaderLogoSize(e.target.value)}
                  aria-label="Logo size percent"
                />
                <span className="embed-appearance-opacity-suffix">%</span>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '24px' }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving || !schoolId}
              onClick={handleSave}
            >
              {saving ? 'Saving...' : 'Save Embed Appearance'}
            </button>
            {success && (
              <div style={{
                marginTop: '12px',
                background: '#ecfdf5',
                color: '#047857',
                border: '1px solid #a7f3d0',
                borderRadius: '12px',
                padding: '12px 14px',
              }}>
                {success}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default EmbedAppearanceSection;
